/**
 * Mobile App 공통 타입 정의
 * @ict/shared에서 가져오는 타입과 모바일 앱 전용 타입 정리
 */

// ============================================
// @ict/shared에서 import (SSOT 준수)
// ============================================
import {
  CHECK_RESULTS as SHARED_CHECK_RESULTS,
  type CheckResultValue as SharedCheckResultValue,
  type ProjectStatus as SharedProjectStatus,
} from "@ict/shared";

// ============================================
// Re-export (기존 import 경로 호환성 유지)
// ============================================
export const CHECK_RESULTS = SHARED_CHECK_RESULTS;
export type CheckResultValue = SharedCheckResultValue;
export type ProjectStatus = SharedProjectStatus;

// ============================================
// Mobile App 전용 확장 타입
// ============================================

/**
 * 점검결과 값 (null 허용)
 * mobile-app에서는 초기 상태로 null을 허용
 */
export type CheckResultValueNullable = SharedCheckResultValue | null;

// 프로젝트 타입 (mobile-app 전용 확장)
export interface Project {
  _id: string;
  number?: string;
  name: string;
  companyId: string;
  type?: string;
  status?: ProjectStatus;
  address?: string;
  memo?: string;
  startDate?: string;
  endDate?: string;
  checkManager?: string;
  author?: string;
  witness?: string;
  createdAt: string;
  updatedAt: string;
}

// 설비 그룹 타입
export interface MachineGroup {
  _id: string;
  projectId: string;
  docKey: string;
  docLabel: string;
  machines: Machine[];
}

// 설비 타입
export interface Machine {
  _id: string;
  machineId: string;
  name: string;
  model?: string;
  manufacturer?: string;
  serialNumber?: string;
}

// 점검항목 타입
export interface CheckListItem {
  _id?: string;
  category: string;
  item: string;
  result: CheckResultValueNullable; // ○, ×, /, null
  note?: string;
  // 점검항목별 사진 필드
  image?: string; // 기본 이미지
  imageO?: string; // 적합(O) 사진
  imageX?: string; // 부적합(X) 사진
  underReason?: string; // 부적합 원인
  underSolution?: string; // 조치 내용
}

// 데이터 항목 타입
export interface DataItem {
  _id?: string;
  category: string;
  item: string;
  value?: string;
  unit?: string;
  note?: string;
}

// 사진 타입
export interface Picture {
  _id: string;
  url: string;
  caption?: string;
  createdAt: string;
}

// 설비 문서 타입 (점검 데이터 포함)
export interface MachineDocument {
  _id: string;
  machineId: string;
  name: string;
  model?: string;
  checkList: CheckListItem[];
  dataList: DataItem[];
  pictures: Picture[];
}

// 사용자 타입
export interface User {
  _id: string;
  id: string;
  name: string;
  grade: string;
  companyId: string;
  companyName: string;
  grants: string[];
}

// 페이지네이션 응답
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}

// 프로젝트 목록 응답
export interface ProjectsResponse {
  projects: Project[];
  total: number;
  page: number;
  limit: number;
}

// 동기화 상태
export type SyncStatus = "pending" | "syncing" | "completed" | "failed";

// 동기화 작업 타입
export type SyncOperation = "create" | "update" | "delete";

// 엔티티 타입
export type EntityType =
  | "project"
  | "machine"
  | "checklist"
  | "data"
  | "photo"
  | "equipment"
  | "measure";
